import { resolveHostInputMediaType } from '../../shared/hostInputMediaTypes';
/**
 * WEB-08 legacy upload migration. The Web adapter's file-picker (dialog.selectFile)
 * and dialog.saveInlineFile used to POST the whole file as base64 to the deprecated
 * /files/upload. They now upload through the chunked /api/inputs protocol into the
 * per-user draft scope, then ask the Host to materialize the committed input to the
 * same target the legacy route used and return that Host path (caller-compatible).
 */
import { randomUuid } from '../utils/randomUuid';
import { browserInputRequest, uploadBrowserHostInput, type BrowserHostInputResult } from './hostInputTransfer';
import { buildHostInputDraftTurnId, HOST_INPUT_DRAFT_SESSION_ID } from './hostInputAttachmentToken';

type InputRequest = (path: string, body: Record<string, unknown>, signal: AbortSignal) => Promise<unknown>;

export type LegacyUploadTarget = { executionMode?: string; sessionId?: string; cwd?: string };

/** Browsers report platform-specific or empty MIME types; normalize to the Host allowlist names. */
export function normalizeLegacyUploadFile(file: File): File {
  const type = resolveHostInputMediaType(file.name, file.type);
  return type === file.type ? file : new File([file], file.name, { type, lastModified: file.lastModified });
}

export async function uploadDraftHostInput(options: {
  file: File; turnId: string; request?: InputRequest; signal?: AbortSignal;
}): Promise<BrowserHostInputResult> {
  return uploadBrowserHostInput({
    file: normalizeLegacyUploadFile(options.file),
    sessionId: HOST_INPUT_DRAFT_SESSION_ID,
    turnId: options.turnId,
    request: options.request,
    signal: options.signal,
  });
}

/** Throws on any failure so callers can fall back to the deprecated /files/upload. */
export async function uploadLegacyFileViaHostInputs(options: {
  file: File; target: LegacyUploadTarget; request?: InputRequest; signal?: AbortSignal;
}): Promise<string> {
  const request = options.request ?? browserInputRequest;
  const signal = options.signal ?? new AbortController().signal;
  const turnId = buildHostInputDraftTurnId(randomUuid());
  const uploaded = await uploadDraftHostInput({ file: options.file, turnId, request, signal });
  const target: Record<string, string> = {};
  for (const key of ['executionMode', 'sessionId', 'cwd'] as const) {
    const value = options.target[key];
    if (typeof value === 'string' && value) target[key] = value;
  }
  const result = await request('/inputs/materialize', {
    sessionId: HOST_INPUT_DRAFT_SESSION_ID, turnId, inputRef: uploaded.inputRef, target,
  }, signal) as { path?: unknown };
  if (!result || typeof result.path !== 'string' || !result.path) {
    throw new Error('Host input materialize response is invalid.');
  }
  return result.path;
}
